import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import type { SxProps, Theme } from '@mui/material/styles';

import { useOpen } from 'src/hooks/use-open';

import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';

import Icons from 'assets/icons';

interface CollapsibleSectionProps {
  title: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
  sx?: SxProps<Theme>;
  readOnly?: boolean;
  'data-testid'?: string;
}

const SECTION_SX: SxProps<Theme> = {
  borderRadius: '0.5rem',
  borderWidth: '1px',
  borderColor: 'tokens.navy.100',
};

const CollapsibleSection = ({
  title,
  defaultOpen = false,
  children,
  headerRight,
  sx,
  readOnly,
  'data-testid': dataTestId,
}: CollapsibleSectionProps): React.JSX.Element => {
  const { isOpen, handleToggle } = useOpen(defaultOpen);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const contentElement = contentRef.current;
    let resizeObserver: ResizeObserver | undefined;

    const updateHeight = (): void => {
      if (contentElement) {
        contentElement.style.height = isOpen
          ? `${contentElement.scrollHeight}px`
          : '0px';
      }
    };

    if (contentElement) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(contentElement);
    }

    updateHeight();

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isOpen]);

  return (
    <Box sx={mergeSx(SECTION_SX, sx)} data-testid={dataTestId}>
      <Box
        component="button"
        type="button"
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            py: 1.5,
            px: 2,
            fontWeight: 700,
            color: 'tokens.gray.700',
            gap: 1.5,
          },
          isOpen && {
            borderBottomWidth: '1px',
            borderColor: 'tokens.navy.100',
          },
        ]}
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <span className="flex items-center gap-3">
          {headerRight}
          <Icons.Arrow
            className={classNames('rotate-90 transition shrink-0', {
              '!-rotate-90': isOpen,
            })}
          />
        </span>
      </Box>
      <div ref={contentRef} className="overflow-hidden transition-all">
        <Box
          sx={[
            { position: 'relative' },
            isOpen && { p: 2 },
            !isOpen && { display: 'none' },
          ]}
          inert={readOnly}
        >
          {children}
        </Box>
      </div>
    </Box>
  );
};

export default CollapsibleSection;
